import along from "@turf/along";
import area from "@turf/area";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import buffer from "@turf/buffer";
import { convex } from "@turf/convex";
import { feature as asFeature, featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import length from "@turf/length";
import { lineSplit } from "@turf/line-split";
import { pointToLineDistance } from "@turf/point-to-line-distance";
import union from "@turf/union";
import type { LineString, MultiLineString, MultiPolygon, Polygon } from "geojson";

import { areaCentroid, geodesicPerimeterMetres, holeCount, orientedRectangleSidesMetres, partCount, pointCount, toLineStrings } from "./geometry";

/** A street near a parcel. `forOverlap` (non-service, within the gate) drives `street_overlap_ratio`. */
export type StreetGeometry = LineString | MultiLineString;

export interface ParcelInput {
  geometry: Polygon | MultiPolygon;
  /** `AddressType` property of the parcel (empty string / null when absent). */
  addressType: string | null;
  /** Non-service streets within the gate distance — used for `street_overlap_ratio`. */
  gateStreets: ReadonlyArray<StreetGeometry>;
  /** All streets near the parcel — used for `centerline_len_m` and the nearest-street distance. */
  nearbyStreets: ReadonlyArray<StreetGeometry>;
}

export interface StreetParcelConfig {
  /** Buffer applied to streets when measuring overlap; PostGIS buffers this many EPSG:3857 units. */
  streetBufferDistanceMetres: number;
}

export const DEFAULT_STREET_PARCEL_CONFIG: StreetParcelConfig = {
  streetBufferDistanceMetres: 10,
};

/** The 14 features the model consumes, by their model feature names. */
export interface StreetParcelFeatures {
  area_m2: number;
  street_overlap_ratio: number | null;
  perimeter_m: number;
  compactness: number | null;
  elongation: number | null;
  width_m: number | null;
  convexity: number | null;
  n_points: number;
  n_parts: number;
  hole_count: number;
  centerline_len_m: number;
  centerline_len_per_perim: number | null;
  centroid_street_dist_norm: number | null;
  addresstype: string;
}

function convexity(geom: Polygon | MultiPolygon, areaM2: number): number | null {
  const hull = convex(asFeature(geom));
  if (!hull) return null;
  const hullArea = area(hull);
  return hullArea > 0 ? areaM2 / hullArea : null;
}

/**
 * Fraction of the parcel covered by buffered streets.
 *
 * PostGIS buffers in EPSG:3857, where 10 units ≈ `10·cos(lat)` ground metres (the Mercator scale
 * is `sec(lat)`); we reproduce that with a geodesic buffer of the corrected distance.
 */
function streetOverlapRatio(
  geom: Polygon | MultiPolygon,
  gateStreets: ReadonlyArray<StreetGeometry>,
  latitude: number,
  config: StreetParcelConfig
): number | null {
  const parcelArea = area(geom);
  if (parcelArea <= 0) return null;
  const metres = config.streetBufferDistanceMetres * Math.cos(latitude * (Math.PI / 180));
  const buffered = gateStreets.map((street) => buffer(street, metres, { units: "meters" })).filter(Boolean);
  if (buffered.length === 0) return 0;
  let merged = buffered[0];
  for (let i = 1; i < buffered.length; i++) {
    const unioned = union(featureCollection([merged, buffered[i]]));
    if (unioned) merged = unioned;
  }
  const overlap = intersect(featureCollection([asFeature(geom), merged]));
  return Math.min((overlap ? area(overlap) : 0) / parcelArea, 1);
}

/**
 * Distance from the parcel centroid to the nearest street, normalised by `sqrt(area)`.
 *
 * Matches `ST_Distance(centroid, nearest street) / sqrt(area)`; the centroid must be the area
 * centroid (see `areaCentroid`), not a vertex mean.
 */
function centroidStreetDistNorm(geom: Polygon | MultiPolygon, nearbyStreets: ReadonlyArray<StreetGeometry>, areaM2: number): number | null {
  const centroid = areaCentroid(geom);
  if (!centroid || areaM2 <= 0) return null;
  let nearest = Infinity;
  for (const street of nearbyStreets) {
    for (const line of toLineStrings(street)) {
      const distance = pointToLineDistance(centroid, line, { method: "geodesic", units: "meters" });
      if (distance < nearest) nearest = distance;
    }
  }
  return nearest === Infinity ? null : nearest / Math.sqrt(areaM2);
}

/** Total length of street centrelines inside the parcel — matches `SUM(ST_Length(street ∩ parcel))`. */
function centerlineLengthMetres(geom: Polygon | MultiPolygon, nearbyStreets: ReadonlyArray<StreetGeometry>): number {
  const parcel = asFeature(geom);
  let total = 0;
  for (const street of nearbyStreets) {
    for (const line of toLineStrings(street)) {
      const lineFeature = asFeature(line);
      let segments;
      try {
        const split = lineSplit(lineFeature, parcel);
        segments = split.features.length > 0 ? split.features : [lineFeature];
      } catch {
        segments = [lineFeature];
      }
      for (const segment of segments) {
        const segmentLengthKm = length(segment, { units: "kilometers" });
        if (segmentLengthKm > 0) {
          const interior = along(segment, segmentLengthKm / 2, { units: "kilometers" });
          if (booleanPointInPolygon(interior, geom)) total += segmentLengthKm * 1000;
        }
      }
    }
  }
  return total;
}

/** Compute all 14 model features for a parcel. */
export function computeStreetParcelFeatures(parcel: ParcelInput, config: StreetParcelConfig = DEFAULT_STREET_PARCEL_CONFIG): StreetParcelFeatures {
  const { geometry } = parcel;
  const areaM2 = area(geometry);
  const perimeterM = geodesicPerimeterMetres(geometry);
  const sides = orientedRectangleSidesMetres(geometry);
  const centroid = areaCentroid(geometry);
  const latitude = centroid ? centroid.coordinates[1] : 0;
  const centerlineLen = centerlineLengthMetres(geometry, parcel.nearbyStreets);

  return {
    area_m2: areaM2,
    street_overlap_ratio: streetOverlapRatio(geometry, parcel.gateStreets, latitude, config),
    perimeter_m: perimeterM,
    compactness: perimeterM > 0 ? (4 * Math.PI * areaM2) / perimeterM ** 2 : null,
    elongation: sides ? Math.max(...sides) / Math.min(...sides) : null,
    width_m: sides ? Math.min(...sides) : null,
    convexity: convexity(geometry, areaM2),
    n_points: pointCount(geometry),
    n_parts: partCount(geometry),
    hole_count: holeCount(geometry),
    centerline_len_m: centerlineLen,
    centerline_len_per_perim: perimeterM > 0 ? centerlineLen / perimeterM : null,
    centroid_street_dist_norm: centroidStreetDistNorm(geometry, parcel.nearbyStreets, areaM2),
    addresstype: parcel.addressType ?? "",
  };
}
